import AudioPlayer from "./../settings-audio-player";
import CircularProgress from "../../actions/CircularProgress";
import actions from "../../actions/actions";
import { AlertType } from "../../utilities/constants";
import * as DOMPurify from "dompurify";
import { useState, useEffect, useRef, useMemo } from "react";

export default function MessageContent({ alert }) {
  const [audioUrls, setAudioUrls] = useState(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showAllBody, setShowAllBody] = useState(false);

  const isMountedRef = useRef(true);
  const bodyRef = useRef(null);

  // Message content sanitization + fallback
  const sanitizedHtml = useMemo(
    () => DOMPurify.sanitize(alert.message || ""),
    [alert.message],
  );
  const plainTextFallback = useMemo(() => {
    if (!alert.message) {
      if (alert.alertType === AlertType.Voice && alert.voiceFileUrl)
        return "(Voice recording message)";
      return "(No message content)";
    }
    return alert.message.replace(/<[^>]+>/g, "").trim();
  }, [alert.message, alert.alertType, alert.voiceFileUrl]);

  useEffect(() => {
    if (bodyRef.current) {
      const height = bodyRef.current.offsetHeight;
      setIsTruncated(height > 1000);
    }
  }, [sanitizedHtml]);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (alert?.alertType !== AlertType.Voice) return;
    if (alert?.voiceFileIdentifier && alert.voiceFileIdentifier !== "Pending") {
      setAudioUrls(alert?.voiceFileUrl);
    } else if (alert?.message) {
      actions.alerts.textToSpeech(alert.message).then((response) => {
        if (response?.success && isMountedRef.current) {
          setAudioUrls(response?.fileUrl);
        }
      });
    }
  }, [
    alert?.alertType,
    alert?.voiceFileIdentifier,
    alert?.voiceFileUrl,
    alert?.message,
  ]);

  return (
    <section className="message-content-section" aria-label="Message Content">
      <div className="message-content-inner">
        <h2 className="message-content-title">Message Content</h2>
        <div className="message-content-field">
          <span className="message-content-label">Message</span>
          <div className="message-content-body">
            {alert?.alertType === AlertType.Voice ? (
              <div
                className="mc-audio-player"
                role="region"
                aria-label="Voice message player"
              >
                {audioUrls ? (
                  <AudioPlayer src={audioUrls} autoPlay={false} />
                ) : (
                  <CircularProgress />
                )}
              </div>
            ) : (
              <div className="ad-message-text">
                {sanitizedHtml ? (
                  <div
                    ref={bodyRef}
                    className={`ad-body-html${
                      isTruncated && !showAllBody ? " truncated" : ""
                    }`}
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  />
                ) : (
                  <div className="ad-body-fallback">{plainTextFallback}</div>
                )}
                {isTruncated && (
                  <button
                    type="button"
                    className="ad-show-toggle"
                    aria-label={
                      showAllBody ? "Show less content" : "Show full content"
                    }
                    onClick={() => setShowAllBody(!showAllBody)}
                  >
                    {showAllBody ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
